import { AnalyticsImpl } from "./AnalyticsImpl";
import { AnalyticsInstance, EVENTS, EventPayloads, TelemetryKeys } from "./Analytics";
import { TelemetryImpl } from "./TelemetryImpl";
import { FeaturesConfig } from "~/backend/generated/Features/model/featuresConfig";
import { Logger } from "~/modules/logger";
import { getFeatureNameById } from "~/utils/getFeatureNameById";
import type { Worker } from "~/packages/taskrouter";
import { ErrorResponse } from "~/modules/error/ThrowError/ErrorHelper";




export interface WriteKeyMap {
    analytics: {
        dev: string;
        stage: string;
        prod: string;
    };
    telemetry: {
        applicationId: string;
        clientToken: string;
    };
}




export const getWriteKeyMap = (encodedWriteKeys: string | undefined): WriteKeyMap => {
    const decodedWriteKeys = atob(encodedWriteKeys ?? "");
    const writeKeysArray = JSON.parse(decodedWriteKeys || "{}") as TelemetryKeys;

    return {
        analytics: {
            dev: writeKeysArray.analyticsDev,
            stage: writeKeysArray.analyticsStage,
            prod: writeKeysArray.analyticsProd
        },
        telemetry: {
            applicationId: writeKeysArray.telemetryApplicationId,
            clientToken: writeKeysArray.telemetryClientToken
        }
    };
};

/**
 * @internal
 */
export const configureSDKTelemetry = (
    analytics: AnalyticsImpl,
    telemetry: TelemetryImpl,
    logger: Logger,
    telemetryDisabled: boolean,
    featuresConfig: FeaturesConfig | undefined,
    accountSid: string
) => {
    if (telemetryDisabled) {
        logger.info("FlexSDK Telemetry is disabled.");
        analytics.init(true);
        telemetry.init(true);
        return;
    }

    if (!featuresConfig) {
        logger.warn("Failed to fetch the features configuration. The FlexSDK Telemetry will be disabled.");
        analytics.init(true);
        telemetry.init(true);
        return;
    }

    const encodedTelemetryConfig = getFeatureNameById("flex-sdk-v3-telemetry", featuresConfig);

    if (!encodedTelemetryConfig) {
        logger.warn("FlexSDK Telemetry configuration not found. The FlexSDK Telemetry will be disabled.");
        analytics.init(true);
        telemetry.init(true);
        return;
    }

    try {
        const telemetryKeyMap = getWriteKeyMap(encodedTelemetryConfig);
        logger.info("Initializing Telemetry");
        analytics.init(false, telemetryKeyMap);
        telemetry.init(false, telemetryKeyMap, accountSid);
    } catch (e) {
        logger.warn("Failed to initialise FlexSDK Telemetry", e);
        analytics.init(true);
        telemetry.init(true);
    }
};

export function sendWorkerTrackingEvent<K extends EVENTS>(
    event: K,
    analytics: AnalyticsInstance,
    worker: Worker,
    extra?: EventPayloads[K]
): void {
    if (!worker) {
        return;
    }
    const properties = {
        accountSid: worker.accountSid,
        userId: worker.sid,
        workerSid: worker.sid,
        ...(extra && { ...extra })
    };

    analytics.track(event, properties);
}

export function sendLifecycleTrackingEvent<K extends EVENTS>(
    event: K,
    analytics: AnalyticsInstance,
    accountSid: string,
    extra?: EventPayloads[K]
): void {
    if (!accountSid) {
        return;
    }
    const properties = {
        accountSid,
        userId: accountSid,
        ...(extra && { ...extra })
    };

    analytics.track(event, properties);
}

export function toCauseMessage(cause: unknown): string | undefined {
    if (typeof cause === "string") {
        return cause;
    }
    if ((cause as ErrorResponse)?.message) {
        const error = cause as ErrorResponse;
        return `${error.message}`;
    }

    const seen = new WeakSet<any>();
    try {
        return JSON.stringify(cause, (_key, val) => {
            if (typeof val === "object" && val !== null) {
                if (seen.has(val)) {
                    return "[Circular]";
                }
                seen.add(val);
            }
            if (typeof val === "bigint") {
                return val.toString();
            }
            
            return val;
        });
    } catch {
        try {
            return String(cause);
        } catch {
            return "[unserializable]";
        }
    }
}
